import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState, type ReactElement, type ReactNode } from "react";
import { Field, Modal, PercentInput, QueryStatus, useToast } from "@cx/ui";
import type { DashboardOptions, SalesOrderStatusRef } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

/** O DO ainda prepara os fatos dos pedidos anteriores ao dashboard (409 `facts_rebuilding`). */
export const isRebuilding = (e: unknown): boolean => e instanceof ApiFailure && e.code === "facts_rebuilding";

/** Consultas dos relatórios: enquanto os fatos são preparados, consulta de novo a cada 3 s (sem o retry padrão). */
export const reportQueryOptions = {
  retry: (n: number, e: unknown) => !isRebuilding(e) && n < 3,
  refetchInterval: (q: { state: { error: unknown } }) => (isRebuilding(q.state.error) ? 3_000 : false),
};

/** Carregando / erro de um relatório; durante o preparo dos fatos, um aviso (não é erro) no lugar do alerta. */
export function ReportStatus({ loading, error }: { loading?: boolean; error?: unknown }): ReactElement {
  if (isRebuilding(error))
    return (
      <div className="alert alert-info m-3" role="status">
        Preparando os indicadores com os pedidos já cadastrados… A tela atualiza sozinha em instantes.
      </div>
    );
  return <QueryStatus loading={loading} error={error} />;
}

/** Situações do módulo e configuração do dashboard (todas as telas do Meu Negócio usam). */
export function useDashboardOptions() {
  const { companyId, api } = useSession();
  return useQuery({
    queryKey: ["dashboard", companyId, "options"],
    queryFn: () => api<DashboardOptions>("/reports/options"),
    staleTime: 60_000,
  });
}

/**
 * Filtro de situações: lista de caixas num painel recolhível. Sem escolha (undefined) valem as situações que a
 * configuração conta como venda.
 */
export function StatusFilter({
  statuses,
  defaults,
  value,
  onChange,
}: {
  statuses: SalesOrderStatusRef[];
  defaults: string[];
  value: string[] | undefined;
  onChange(ids: string[] | undefined): void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const panel = useId();
  const chosen = value ?? defaults;
  const toggle = (id: string) => {
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
    if (next.length) onChange(next);
  };
  const summary =
    value === undefined
      ? "Situações de venda (padrão)"
      : chosen.length === 1
        ? (statuses.find((s) => s.id === chosen[0])?.label ?? "1 situação")
        : `${chosen.length} situações`;
  return (
    <div className="position-relative">
      <span className="form-label small d-block mb-2">Situações</span>
      <button
        type="button"
        className="btn btn-outline-secondary w-100 text-start text-truncate"
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen(!open)}
      >
        {summary}
      </button>
      {open && (
        <div
          id={panel}
          className="card shadow position-absolute z-3 mt-1 p-2"
          style={{ minWidth: "16rem", maxHeight: "20rem", overflowY: "auto" }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
        >
          <fieldset>
            <legend className="visually-hidden">Situações dos pedidos</legend>
            {statuses.map((s) => (
              <div className="form-check" key={s.id}>
                <input
                  id={`${panel}-${s.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={chosen.includes(s.id)}
                  onChange={() => toggle(s.id)}
                />
                <label className="form-check-label" htmlFor={`${panel}-${s.id}`}>
                  {s.label}
                </label>
              </div>
            ))}
          </fieldset>
          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-sm btn-link px-0"
              onClick={() => {
                onChange(undefined);
                setOpen(false);
              }}
            >
              Usar as da configuração
            </button>
            <button type="button" className="btn btn-sm btn-primary ms-auto" onClick={() => setOpen(false)}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Configurações do dashboard (situações que contam como venda, imposto e taxa): só com dashboard:update. */
export function SettingsButton(): ReactElement | null {
  const { me } = useSession();
  const [open, setOpen] = useState(false);
  if (!can(me.permissions as Permissions, "dashboard", "update")) return null;
  return (
    <>
      <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(true)}>
        Configurações
      </button>
      {open && <SettingsDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function SettingsDialog({ onClose }: { onClose(): void }): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const options = useDashboardOptions();
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [v, setV] = useState<DashboardOptions["settings"] | null>(null);
  const value = v ?? options.data?.settings ?? null;
  const save = useMutation({
    mutationFn: () => api("/reports/settings", { method: "PUT", body: JSON.stringify(value) }),
    onSuccess: () => {
      toast("success", "Configurações salvas");
      void qc.invalidateQueries({ queryKey: ["dashboard", companyId] });
      void qc.invalidateQueries({ queryKey: ["goals", companyId] });
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const toggle = (id: string) =>
    value &&
    setV({
      ...value,
      saleStatusIds: value.saleStatusIds.includes(id)
        ? value.saleStatusIds.filter((x) => x !== id)
        : [...value.saleStatusIds, id],
    });
  return (
    <Modal
      open={open}
      title="Configurações dos dashboards"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!value || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      {value && options.data && (
        <div className="row">
          <fieldset className="col-12 mb-3">
            <legend className="form-label fs-6">Situações que contam como venda</legend>
            {options.data.statuses.map((s) => (
              <div className="form-check" key={s.id}>
                <input
                  id={`cfg-st-${s.id}`}
                  className="form-check-input"
                  type="checkbox"
                  checked={value.saleStatusIds.includes(s.id)}
                  onChange={() => toggle(s.id)}
                />
                <label className="form-check-label" htmlFor={`cfg-st-${s.id}`}>
                  {s.label}
                </label>
              </div>
            ))}
            {errors.saleStatusIds && <div className="invalid-feedback d-block">{errors.saleStatusIds}</div>}
          </fieldset>
          <Field
            label="Impostos sobre a venda"
            className="col-md-6"
            error={errors.taxPct4}
            hint="Estimativa usada na DRE resumida"
          >
            <PercentInput value={value.taxPct4} onChange={(p) => setV({ ...value, taxPct4: p ?? 0 })} />
          </Field>
          <Field
            label="Taxas de venda"
            className="col-md-6"
            error={errors.salesFeePct4}
            hint="Cartão, marketplace…"
          >
            <PercentInput
              value={value.salesFeePct4}
              onChange={(p) => setV({ ...value, salesFeePct4: p ?? 0 })}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}

/** Linha de filtros (box do Coup). */
export const FilterRow = ({ children }: { children: ReactNode }) => (
  <div className="box card mb-3">
    <div className="row g-2 p-3 align-items-end">{children}</div>
  </div>
);
